import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { walletService } from '../services';
import { formatCurrency } from '../utils/formatters';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import CountdownTimer from '../components/ui/CountdownTimer';
import { useTranslation } from 'react-i18next';
import { 
  FaWallet, FaExchangeAlt, FaUsers, FaMoneyBillWave, FaSyncAlt, 
  FaChartLine, FaRocket, FaShieldAlt, FaGem, FaUser, FaTree, 
  FaStar, FaGift, FaCheck, FaCopy, FaFireAlt, FaBolt, FaLock
} from 'react-icons/fa';

// Lazy loading pour les composants non critiques
const ExchangeRates = lazy(() => import('../components/ExchangeRates'));
const CurrencyConverter = lazy(() => import('../components/CurrencyConverter'));
const Testimonials = lazy(() => import('../components/Testimonials'));
const Features = lazy(() => import('../components/Features'));
const ReferralSection = lazy(() => import('../components/ReferralSection'));
const HoneypotAlertBanner = lazy(() => import('../components/common/HoneypotAlertBanner'));

// Configuration du thème centralisée
const THEME = {
  colors: {
    primary: '#00A04A',
    secondary: '#7AC142',
    accent: '#E8F5E9',
    cosmic: '#1A237E',
    cosmicSecondary: '#303F9F',
    cosmicAccent: '#7986CB',
    speed: '#FF6F00',
    speedSecondary: '#FFA000',
    speedAccent: '#FFD54F'
  },
  gradients: {
    primary: 'from-[#00A04A] via-[#303F9F] to-[#FF6F00]',
    secondary: 'from-[#7AC142] via-[#7986CB] to-[#FFA000]',
    dark: 'from-[#0D1B2A] via-[#1B263B] to-[#0D1B2A]'
  }
};

// Animations réutilisables
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const scaleOnHover = {
  whileHover: { scale: 1.05 },
  transition: { type: "spring", stiffness: 400, damping: 10 }
};

// Composant pour le fond dégradé subtil
const BackgroundGradient = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-[#0D1B2A] dark:via-[#1B263B] dark:to-[#0D1B2A]"></div>
  </div>
));

// Composant pour les statistiques clés du lancement
const LaunchStats = React.memo(() => {
  const stats = [
    { value: "50K+", label: "Pré-inscrits", icon: FaUsers, color: THEME.colors.cosmicSecondary },
    { value: "500M", label: "Supply BBT", icon: FaTree, color: THEME.colors.primary },
    { value: "0% Fees", label: "Premier Mois", icon: FaGift, color: THEME.colors.speed },
    { value: "24/7", label: "Support", icon: FaShieldAlt, color: THEME.colors.secondary }
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
      {stats.map((stat, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 + index * 0.1 }}
          className="text-center p-4 bg-[#0D1B2A]/40 rounded-lg hover:bg-[#0D1B2A]/60 transition-all duration-300"
        >
          <stat.icon className="text-2xl mx-auto mb-2" style={{ color: stat.color }} />
          <p className="text-2xl font-bold text-white">{stat.value}</p>
          <p className="text-xs text-slate-400">{stat.label}</p>
        </motion.div>
      ))}
    </div>
  );
});

// Composant pour les données principales BBT
const BBTStats = React.memo(() => {
  const mainStats = [
    { label: "Prix de Lancement", value: "~$0.000438", color: THEME.colors.secondary },
    { label: "Market Cap Initial", value: "$219K", color: "white" },
    { label: "Supply en Circulation", value: "100,000 BBT", color: "white" },
    { label: "Réseau", value: "BSC", color: THEME.colors.secondary },
    { label: "Holders Initiaux", value: "4", color: "white" },
    { label: "Liquidité", value: "Verrouillée", color: THEME.colors.secondary, icon: FaLock }
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {mainStats.map((stat, index) => (
        <div key={index} className="bg-[#0D1B2A]/50 rounded-lg p-4 border border-[#7AC142]/10">
          <p className="text-gray-400 mb-1 text-sm">{stat.label}</p>
          <p className="text-lg font-bold" style={{ color: stat.color }}>{stat.value}</p>
        </div>
      ))}
    </div>
  );
});

// Composant pour les fonctionnalités principales
const MainFeatures = React.memo(() => {
  const { t } = useTranslation();
  
  const features = [
    {
      title: t('home.features.multiCurrency.title', 'Multi-Devises'),
      description: t('home.features.multiCurrency.desc', 'Échangez entre RUB, XOF, XAF, USD et cryptomonnaies (USDT, BBT)'),
      icon: <FaExchangeAlt className="text-[#7AC142] text-3xl" />
    },
    {
      title: t('home.features.ecosystem.title', 'Écosystème Baobab'),
      description: t('home.features.ecosystem.desc', 'Un portefeuille sécurisé enraciné dans la technologie blockchain'),
      icon: <FaTree className="text-[#7AC142] text-3xl" />
    },
    {
      title: t('home.features.speed.title', 'Vitesse Cosmique'),
      description: t('home.features.speed.desc', 'Transactions ultra-rapides avec confirmation instantanée'),
      icon: <FaRocket className="text-[#FF6F00] text-3xl" />
    }
  ];

  return (
    <div className="grid md:grid-cols-3 gap-6 w-full max-w-5xl mx-auto">
      {features.map((feature, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-[#1B263B]/50 rounded-xl p-6 backdrop-blur-sm hover:bg-[#1B263B]/70 transition-all duration-300 border border-[#1B263B]/30 hover:border-[#7986CB]/30 hover:transform hover:scale-105"
        >
          <div className="mb-4">{feature.icon}</div>
          <h3 className="text-xl font-semibold mb-2 text-white">{feature.title}</h3>
          <p className="text-gray-400">{feature.description}</p>
        </motion.div>
      ))}
    </div>
  );
});

const Home = () => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    balances: {},
    recentTransactions: [],
    stats: {
      totalTransactions: 0,
      pendingTransactions: 0,
      totalConverted: 0,
      referralEarnings: 0
    }
  });
  const { t } = useTranslation();

  // Chargement optimisé des données du dashboard
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [balanceData, transactionsData] = await Promise.all([
          walletService.getBalance(),
          walletService.getTransactions({ limit: 5, page: 1 })
        ]);

        setDashboardData({
          balances: balanceData,
          recentTransactions: transactionsData.transactions || [],
          stats: {
            totalTransactions: transactionsData.stats?.totalCount || 0,
            pendingTransactions: transactionsData.stats?.pendingCount || 0,
            totalConverted: transactionsData.stats?.totalConverted || 0,
            referralEarnings: 0
          }
        });
      } catch (error) {
        console.error('Erreur lors du chargement des données du tableau de bord:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthenticated, user]);

  return (
    <>
      <div className="min-h-screen bg-white dark:bg-[#0D1B2A] flex flex-col">
        <BackgroundGradient />
        
        <div className="flex-grow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-24 relative z-10">
            
            <Suspense fallback={null}>
              <HoneypotAlertBanner />
            </Suspense>
            
            {/* En-tête principal optimisé */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center relative"
            >
              {/* Logo simplifié */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="mx-auto mb-8"
              >
                <img 
                  src="/images/logo.png" 
                  alt="Baobab Exchange"
                  className="h-20 mx-auto rounded-lg object-contain"
                />
              </motion.div>
              
              <motion.div 
                className="relative z-10 px-8 py-12 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
              >
                
                <motion.h1 
                  className="text-5xl md:text-7xl font-bold mb-8 text-slate-900 dark:text-white"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  {t('home.welcome.title.line1', 'Bienvenue sur')} <br />
                  <span className="text-primary">
                    {t('home.welcome.title.appName', 'Baobab Exchange')}
                  </span>
                </motion.h1>
                
                <motion.p 
                  className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto leading-relaxed text-slate-600 dark:text-slate-300"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  {t('home.welcome.subtitle', 'Transferts d\'argent rapides et sécurisés entre la Russie et l\'Afrique.')}
                </motion.p>

                {/* Boutons d'action */}
                {!isAuthenticated && (
                  <motion.div 
                    className="flex flex-col sm:flex-row items-center justify-center gap-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <Link
                      to="/register"
                      className="btn-primary-lg flex items-center gap-2"
                    >
                      <FaUser />
                      {t('home.welcome.createAccountButton', 'Créer un compte')}
                    </Link>
                    <Link
                      to="/login"
                      className="btn-secondary flex items-center gap-2 h-12 px-6"
                    >
                      <FaExchangeAlt />
                      {t('home.welcome.loginButton', 'Se connecter')}
                    </Link>
                  </motion.div>
                )}
              </motion.div>
              
              {/* Badge de sécurité */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-8 flex justify-center"
              >
                <div className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full py-2 px-4 flex items-center text-sm text-slate-700 dark:text-slate-300">
                  <FaShieldAlt className="text-primary mr-2" />
                  {t('home.welcome.securityBadge', 'Plateforme 100% sécurisée')}
                </div>
              </motion.div>
            </motion.div>

            {/* Section de Lancement optimisée */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.4 }}
              className="relative overflow-hidden"
            >
              <div className="max-w-5xl mx-auto relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00A04A]/10 via-[#303F9F]/10 to-[#FF6F00]/10 rounded-3xl blur-xl"></div>
                
                <div className="relative bg-gradient-to-br from-[#0D1B2A]/90 to-[#1B263B]/90 backdrop-blur-xl rounded-2xl p-8 border border-[#7AC142]/20 overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#00A04A]/50 to-transparent"></div>
                  
                  <div className="text-center mb-8">
                    <h2 className="text-3xl md:text-4xl font-bold mb-4 relative inline-block">
                      <span className="relative z-10 text-white">Lancement Réussi !</span>
                      <div className="absolute -inset-1 bg-gradient-to-r from-[#A7F3D0]/50 via-[#FDE68A]/50 to-[#FECACA]/50 blur-xl rounded-lg z-0 opacity-100"></div>
                    </h2>
                    <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                      Le Baobab Token (BBT) est maintenant live sur le Mainnet BSC. La liquidité initiale est faible, conçue pour nos premiers supporters.
                    </p>
                  </div>
                  
                  {/* Compteurs - Peut être remplacé par des stats réelles */}
                  <div className="text-center mb-8">
                    <p className="text-green-400 font-bold">Trading Actif sur PancakeSwap</p>
                  </div>
                  
                  {/* Actions */}
                  <div className="text-center flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="https://pancakeswap.finance/swap?outputCurrency=0x0354a58290E141b241d5b32AE02D581289f286b7"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold text-lg transform transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-amber-500/30"
                    >
                      <FaRocket className="mr-2" />
                      Acheter sur PancakeSwap
                    </a>
                    <Link
                      to="/bbt"
                      className="inline-block px-8 py-4 bg-gradient-to-r from-[#00A04A] to-[#7AC142] text-white rounded-xl font-medium text-lg transform transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-[#00A04A]/30"
                    >
                      Plus d'infos sur BBT
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Annonce Baobab DEX */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.45 }}
              className="relative"
            >
              <div className="max-w-5xl mx-auto relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00A04A]/15 via-[#303F9F]/10 to-[#7AC142]/15 rounded-3xl blur-xl"></div>
                
                <div className="relative bg-gradient-to-br from-[#0D1B2A]/90 to-[#1B263B]/90 backdrop-blur-xl rounded-2xl p-8 border border-[#00A04A]/30 overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#00A04A] via-[#7AC142] to-[#00A04A]"></div>
                  
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <span className="bg-[#00A04A]/20 text-[#7AC142] text-xs font-bold px-3 py-1 rounded-full border border-[#7AC142]/30 flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      LIVE ON BSC MAINNET
                    </span>
                  </div>
                  
                  <div className="text-center mb-6">
                    <h2 className="text-3xl md:text-4xl font-bold mb-3 text-white flex items-center justify-center gap-3">
                      <FaExchangeAlt className="text-[#7AC142]" />
                      Baobab DEX
                    </h2>
                    <p className="text-lg text-slate-300 max-w-2xl mx-auto">
                      Notre exchange decentralise est en ligne ! Echangez vos tokens directement sur BSC avec des frais minimaux.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-lg mx-auto">
                    <a
                      href="https://bscscan.com/address/0x83cAfA082F13cd65Bf979a56405c2B5d8cF69BF6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-[#0D1B2A]/60 rounded-lg border border-[#7AC142]/10 hover:border-[#7AC142]/30 transition-colors"
                    >
                      <span className="text-sm text-gray-400">Factory</span>
                      <span className="text-xs text-[#7AC142] font-mono">0x83cA...BF6</span>
                    </a>
                    <a
                      href="https://bscscan.com/address/0xb4E11bc5ca54b7eCC7cBaE9dbdCc4c3F97E6BAA0"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 bg-[#0D1B2A]/60 rounded-lg border border-[#7AC142]/10 hover:border-[#7AC142]/30 transition-colors"
                    >
                      <span className="text-sm text-gray-400">Router</span>
                      <span className="text-xs text-[#7AC142] font-mono">0xb4E1...AA0</span>
                    </a>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                      to="/dex/swap"
                      className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#00A04A] to-[#7AC142] text-white rounded-xl font-bold text-lg transform transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-[#00A04A]/30"
                    >
                      <FaExchangeAlt className="mr-2" />
                      Swap Tokens
                    </Link>
                    <Link
                      to="/dex/liquidity/add"
                      className="inline-flex items-center justify-center px-8 py-4 bg-[#1B263B]/70 text-white rounded-xl font-bold text-lg border border-[#7AC142]/20 transform transition-all duration-200 hover:scale-105 hover:bg-[#1B263B]"
                    >
                      <FaWallet className="mr-2" />
                      Ajouter Liquidite
                    </Link>
                    <a
                      href="https://analytics.baobabexchange.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-8 py-4 bg-[#303F9F]/20 text-white rounded-xl font-bold text-lg border border-[#7986CB]/20 transform transition-all duration-200 hover:scale-105 hover:bg-[#303F9F]/30"
                    >
                      <FaChartLine className="mr-2" />
                      Analytics
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Section BBT Token optimisée */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.5 }}
              className="relative"
            >
              <div className="max-w-6xl mx-auto bg-gradient-to-br from-[#1B263B]/95 to-[#0D1B2A]/95 backdrop-blur-xl rounded-2xl p-8 border border-[#7AC142]/20 shadow-2xl hover:shadow-[#7AC142]/10 transition-all duration-300 overflow-hidden">
                
                <div className="absolute top-4 right-4 bg-gradient-to-r from-[#00A04A] to-[#7AC142] text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-1">
                  <FaRocket className="text-sm" />
                  LANCÉ !
                </div>
                
                <div className="text-center mb-8">
                  <motion.h2 
                    className="text-4xl md:text-5xl font-bold mb-4 relative flex items-center justify-center gap-3"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    <FaTree className="text-3xl md:text-4xl text-[#7AC142]" />
                    <span className="relative z-10 text-white">
                      Baobab Token (BBT)
                    </span>
                    <FaTree className="text-3xl md:text-4xl text-[#7AC142]" />
                  </motion.h2>
                  <p className="text-xl text-[#7AC142] font-semibold mb-2 flex items-center justify-center gap-2">
                    <FaCheck className="text-lg" />
                    DÉPLOYÉ AVEC SUCCÈS SUR BSC MAINNET
                  </p>
                  <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                    Le token utilitaire de Baobab Exchange est maintenant live ! Profitez de frais réduits, de récompenses exclusives et d'un accès prioritaire.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* Informations du contrat */}
                  <div className="space-y-6">
                    <div className="bg-[#0D1B2A]/50 rounded-xl p-6 border border-[#7AC142]/20">
                      <h3 className="text-xl font-semibold text-white mb-3 flex items-center">
                        <FaShieldAlt className="text-[#7AC142] mr-2" />
                        Contrat Vérifié
                      </h3>
                      <div className="bg-[#0D1B2A]/70 rounded-lg p-4 border border-[#7AC142]/10">
                        <p className="text-sm text-gray-400 mb-2">Adresse du contrat BSC :</p>
                        <p className="text-[#7AC142] font-mono text-sm break-all bg-[#1B263B]/50 p-2 rounded border">
                          0x0354a58290E141b241d5b32AE02D581289f286b7
                        </p>
                        <div className="flex gap-2 mt-3">
                          <a 
                            href="https://bscscan.com/token/0x0354a58290E141b241d5b32AE02D581289f286b7" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs bg-[#7AC142]/20 text-[#7AC142] px-3 py-1 rounded hover:bg-[#7AC142]/30 transition-colors flex items-center gap-1"
                          >
                            <FaChartLine />
                            Voir sur BscScan
                          </a>
                          <button 
                            onClick={() => navigator.clipboard.writeText('0x0354a58290E141b241d5b32AE02D581289f286b7')}
                            className="text-xs bg-[#303F9F]/20 text-[#7986CB] px-3 py-1 rounded hover:bg-[#303F9F]/30 transition-colors flex items-center gap-1"
                          >
                            <FaCopy />
                            Copier
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Supply total */}
                    <div className="bg-[#0D1B2A]/50 rounded-xl p-6 border border-[#7AC142]/20">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative"
                      >
                        <h1 className="text-4xl font-bold mb-2 p-4 bg-gradient-to-br from-[#0D1B2A]/80 to-[#1B263B]/60 rounded-xl backdrop-blur-sm flex items-center justify-between shadow-lg border border-[#7AC142]/20">
                          <span className="relative z-10 text-white">500,000,000</span>
                          <span className="text-[#7AC142] text-3xl px-3 py-1 bg-[#0D1B2A]/70 rounded-lg border border-[#7AC142]/30 font-black">BBT</span>
                        </h1>
                        <p className="text-gray-300 text-sm">Supply Total du Baobab Token</p>
                      </motion.div>
                    </div>

                    {/* Avantages */}
                    <div className="bg-[#0D1B2A]/50 rounded-xl p-6 border border-[#7AC142]/20">
                      <h3 className="text-xl font-semibold text-white flex items-center mb-4">
                        <FaStar className="text-[#7AC142] mr-2" />
                        Avantages Exclusifs
                      </h3>
                      <ul className="space-y-3">
                        {[
                          { icon: FaFireAlt, text: '50% de réduction sur les frais' },
                          { icon: FaGem, text: 'Récompenses d\'affiliation x2' },
                          { icon: FaBolt, text: 'Accès prioritaire aux features' },
                          { icon: FaGift, text: 'Airdrops exclusifs' }
                        ].map((avantage, index) => (
                          <motion.li
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className="flex items-center gap-3 text-gray-300"
                          >
                            <avantage.icon className="text-[#7AC142] text-lg" />
                            <span>{avantage.text}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Stats et actions */}
                  <div className="space-y-6">
                    <BBTStats />

                    {/* Actions */}
                    <div className="space-y-4">
                      <Link
                        to="/bbt"
                        className="w-full text-center px-6 py-4 bg-gradient-to-r from-[#00A04A] to-[#7AC142] rounded-xl font-bold text-lg hover:opacity-90 transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-[#00A04A]/30 text-white flex items-center justify-center gap-2"
                      >
                        <FaRocket />
                        Gérer mes BBT
                      </Link>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <Link
                          to="/tokenomics"
                          className="text-center px-4 py-3 bg-[#1B263B]/50 rounded-lg font-medium hover:bg-[#303F9F]/30 transition-colors text-white border border-[#7986CB]/20 flex items-center justify-center gap-2"
                        >
                          <FaChartLine />
                          Tokenomics
                        </Link>
                        <a
                          href="https://pancakeswap.finance/swap?outputCurrency=0x0354a58290E141b241d5b32AE02D581289f286b7"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-center px-4 py-3 bg-[#FF6F00]/20 rounded-lg font-medium hover:bg-[#FF6F00]/30 transition-colors text-white border border-[#FFA000]/20 flex items-center justify-center gap-2"
                        >
                          <FaExchangeAlt />
                          PancakeSwap
                        </a>
                      </div>
                    </div>

                    {/* Offre spéciale */}
                    <div className="bg-gradient-to-r from-[#00A04A]/10 to-[#7AC142]/10 rounded-xl p-4 border border-[#7AC142]/20">
                      <p className="text-center text-sm text-[#7AC142] font-semibold mb-2 flex items-center justify-center gap-2">
                        <FaGift />
                        OFFRE DE LANCEMENT
                      </p>
                      <p className="text-center text-white text-sm">
                        Connectez votre wallet et recevez <span className="font-bold text-[#7AC142]">100 BBT gratuits</span> pour tester la plateforme !
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Autres sections avec lazy loading */}
            <Suspense fallback={<LoadingSpinner />}>
              {/* Taux de change */}
              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.6 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-[#303F9F]/20 to-transparent blur-3xl"></div>
                <div className="relative bg-[#1B263B]/90 backdrop-blur-xl rounded-2xl p-8 border-2 border-[#1B263B]/50 shadow-xl">
                  <h2 className="text-3xl font-bold text-white mb-8 text-center flex items-center justify-center">
                    <FaChartLine className="text-[#7986CB] mr-3" />
                    Taux de change en direct
                  </h2>
                  <div className="bg-[#0D1B2A]/60 rounded-xl p-6 shadow-inner">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="flex items-center justify-between p-4 bg-[#1B263B]/70 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img src="https://flagcdn.com/32x24/ru.png" alt="Drapeau Russie" className="w-8 h-6 rounded object-cover border-2 border-[#1B263B]/50" />
                          <div>
                            <span className="text-white font-semibold">RUB</span>
                            <span className="text-slate-400 text-sm block">Rouble russe</span>
                          </div>
                        </div>
                        <span className="text-2xl font-bold text-white">1.00</span>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-[#1B263B]/70 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img src="https://flagcdn.com/32x24/sn.png" alt="Drapeau Sénégal" className="w-8 h-6 rounded object-cover border-2 border-[#1B263B]/50" />
                          <div>
                            <span className="text-white font-semibold">XOF</span>
                            <span className="text-slate-400 text-sm block">Franc CFA</span>
                          </div>
                        </div>
                        <span className="text-2xl font-bold text-[#7AC142]">6.85</span>
                      </div>
                    </div>
                    <ExchangeRates />
                  </div>
                </div>
              </motion.div>

              {/* Fonctionnalités principales */}
              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.65 }}
                className="relative"
              >
                <MainFeatures />
              </motion.div>

              {/* CTA principal */}
              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.7 }}
                className="mt-8 text-center w-full"
              >
                {isAuthenticated ? (
                  <Link
                    to="/transfer-money"
                    className="px-8 py-4 bg-gradient-to-r from-[#00A04A] to-[#7AC142] text-white rounded-xl font-medium text-lg transform transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-[#00A04A]/30 inline-flex items-center"
                  >
                    <FaExchangeAlt className="mr-2" />
                    Effectuer un transfert
                  </Link>
                ) : (
                  <Link
                    to="/register"
                    className="px-8 py-4 bg-gradient-to-r from-[#00A04A] to-[#7AC142] text-white rounded-xl font-medium text-lg transform transition-all duration-200 hover:scale-105 hover:shadow-lg hover:shadow-[#00A04A]/30 inline-flex items-center"
                  >
                    <FaExchangeAlt className="mr-2" />
                    Commencer à échanger
                  </Link>
                )}
              </motion.div>

              {/* Statistiques */}
              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.75 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#00A04A]/15 via-[#303F9F]/15 to-[#FF6F00]/15 blur-3xl"></div>
                <div className="relative bg-[#1B263B]/90 backdrop-blur-xl rounded-2xl p-8 border-2 border-[#1B263B]/50 shadow-xl">
                  <h2 className="text-3xl font-bold text-white mb-8 text-center">
                    Nos chiffres clés
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {[
                      { title: "150M+", subtitle: "Volume d'échanges", icon: FaExchangeAlt, color: THEME.colors.secondary },
                      { title: "50K+", subtitle: "Utilisateurs", icon: FaUsers, color: THEME.colors.cosmicAccent },
                      { title: "2.5M+", subtitle: "Transactions", icon: FaMoneyBillWave, color: THEME.colors.speedAccent },
                      { title: "100%", subtitle: "Sécurisé", icon: FaGem, color: THEME.colors.secondary }
                    ].map((stat, index) => (
                      <motion.div
                        key={index}
                        initial={{ scale: 0.95 }}
                        whileHover={{ scale: 1.05 }}
                        className="bg-[#0D1B2A]/40 p-6 rounded-xl border-2 border-[#0D1B2A]/40 shadow-lg hover:border-opacity-30 transition-all duration-300"
                        style={{ '--hover-color': stat.color }}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="p-3 rounded-lg" style={{ backgroundColor: `${stat.color}30` }}>
                            <stat.icon className="text-2xl" style={{ color: stat.color }} />
                          </div>
                          <span className="text-sm font-medium" style={{ color: stat.color }}>
                            {index === 0 ? 'Total' : index === 1 ? 'Actifs' : index === 2 ? 'Quotidien' : 'Fiable'}
                          </span>
                        </div>
                        <h3 className="text-3xl font-bold text-white mb-2">{stat.title}</h3>
                        <p className="text-white font-medium">{stat.subtitle}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Section d'affiliation */}
              <ReferralSection />

              {/* Convertisseur */}
              <motion.div
                {...fadeInUp}
                transition={{ delay: 0.85 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-[#00A04A]/20 to-transparent blur-3xl"></div>
                <div className="relative bg-[#1B263B]/90 backdrop-blur-xl rounded-2xl p-8 border-2 border-[#1B263B]/50 shadow-xl">
                  <h2 className="text-3xl font-bold text-white mb-8 text-center flex items-center justify-center">
                    <FaSyncAlt className="text-[#7AC142] mr-3" />
                    Convertisseur de devises
                  </h2>
                  <CurrencyConverter />
                </div>
              </motion.div>
              
              {/* Témoignages */}
              <Testimonials />
            </Suspense>
          </div>
        </div>

        {/* Styles CSS optimisés */}
        <style jsx>{`
          @keyframes twinkle {
            0%, 100% { opacity: 0.2; }
            50% { opacity: 0.8; }
          }
          
          .stars-container div {
            animation: twinkle infinite;
          }
          
          @keyframes pulse-slow {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.03); }
          }
          
          .animate-pulse-slow {
            animation: pulse-slow 6s infinite;
          }
        `}</style>
      </div>
    </>
  );
};

export default Home;
